import { useAuth0 } from "@auth0/auth0-react";
import { type FC, useEffect, useMemo } from "react";
import { setOptimizelyUser } from "../../server/optimizely";
import { useClaims } from "../generic/hooks/use-claims";
import { useAnalyticsData } from "./hooks/use-analytics-data";
import { usePendo } from "./hooks/use-pendo-watch";

interface AccordionState<T> {
  channel?: T;
  details?: T;
  to?: T;
  when?: T;
  message?: T;
}

type EventNames =
  | Gtag.EventNames
  | "save_draft"
  | "save_draft_error"
  | "send_test"
  | "send_test_error"
  | "publish"
  | "publish_error"
  | "unpublish"
  | "create_moment"
  | "edit_moment"
  | "view_moment"
  | "pause_moment"
  | "archive_moment"
  | "unarchive_moment"
  | "copy_moment"
  | "accordion"
  | "launch_library_moment"
  | "discover_search"
  | "discover_branding"
  | "discover_carousel"
  | "discover_hero"
  | "discover_preview"
  | "discover_programs"
  | "carousel"
  | "inspire_me"
  | "content_type_generation"
  | "multiformat_button";

type AccordionEventParams = {
  accordion?: keyof AccordionState<boolean>;
  open?: boolean;
};

type AuthEventParams = {
  user_id?: string;
  org_id?: string;
};

type MomentEventParams = {
  moment_id?: string;
  program_slug?: string;
  template_slug?: string;
  content_type?: string;
};

interface GenericEventParams {
  type?: string;
  value?: string | number | boolean;
  action?: string;
}

type EventParams = Omit<Gtag.EventParams, "value"> &
  GenericEventParams &
  (AccordionEventParams | AuthEventParams | MomentEventParams);

type AnalyticsHelpers = {
  gaEvent: (name: EventNames, eventParams?: EventParams) => void;
};

export const useAnalytics = (): AnalyticsHelpers => {
  const gaEvent: AnalyticsHelpers["gaEvent"] = (name, eventParams): void => {
    try {
      window.gtag("event", name, eventParams);
    } catch {
      // ignore
    }
  };

  return { gaEvent };
};

export const AnalyticsMetadata: FC = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const user = useClaims();

  useEffect(() => {
    if (!user) return;
    void getAccessTokenSilently().then(setOptimizelyUser).catch(console.error);

    gtag("config", "G-DQGMRLDQSN", {
      user_id: user.sub ?? null,
      user_properties: {
        org_id: user["http://changeengine.com/account_id"],
        ce_admin: user["http://changeengine.com/ce_admin"],
        super_admin: user["http://changeengine.com/super_admin"],
      },
      traffic_type: user["http://changeengine.com/ce_admin"] ? "internal" : "external",
    });
  }, [getAccessTokenSilently, user]);

  if (!isAuthenticated) return null;

  return <PendoMetadata />;
};

const PendoMetadata: FC = () => {
  const { user } = useAuth0();
  const claims = useClaims();
  const { data, loading } = useAnalyticsData(claims?.sub, claims?.role, user?.email);
  const pendo = usePendo();

  useEffect(() => {
    if (loading || !data || !pendo) return;
    pendo.initialize(data);
  }, [data, loading, pendo]);

  return null;
};

const PENDO_TRACKING_ATTRIBUTE = "data-analytics-id";

export type ElementTracking = {
  analyticsCategory?: `email_design`;
};

export const useElementTracking = (
  analyticsCategory: ElementTracking["analyticsCategory"],
  ...labels: (string | number | null | undefined)[]
): { [PENDO_TRACKING_ATTRIBUTE]?: string } =>
  useMemo(() => {
    if (!analyticsCategory) return {};

    const name = [analyticsCategory, ...labels]
      .filter(Boolean)
      .map(String)
      .join("-")
      .replace(/[^a-z0-9-]/gi, "-");
    return {
      [PENDO_TRACKING_ATTRIBUTE]: name,
    };
    // Ignore the exhaustive-deps warning as we only want to run this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
